import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { authedJson } from '@/lib/http';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatDateCompact } from '@/lib/dateUtils';

interface OrderRow {
  orderId: string;
  orderType: string;
  status: string;
  grossAmountCents: number;
  netAmountCents: number;
  createdAt: string;
  classTitle: string | null;
  buyerEmail: string | null;
  buyerFirstName: string | null;
  buyerLastName: string | null;
  stripePaymentIntentId: string | null;
  refundTotalCents: number | null;
}

const asArray = <T,>(v: unknown): T[] =>
  Array.isArray(v) ? (v as T[]) : (((v as { data?: T[] } | null)?.data ?? []) as T[]);
const usd = (c: number | null) => `$${((c ?? 0) / 100).toFixed(2)}`;

export function OrdersLookup() {
  const [q, setQ] = useState('');
  const { data, isLoading } = useQuery<unknown>({
    queryKey: ['/api/admin/financials/order-lookup', q],
    queryFn: () => authedJson(`/api/admin/financials/order-lookup?q=${encodeURIComponent(q.trim())}`),
    enabled: q.trim().length >= 2,
  });
  const rows = asArray<OrderRow>(data);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Order lookup</CardTitle>
        <CardDescription>Find an order by its id or the buyer's email.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder="Order id or buyer email…"
          value={q}
          onChange={e => setQ(e.target.value)}
          aria-label="Order lookup"
        />
        {q.trim().length < 2 ? (
          <p className="text-sm text-muted-foreground">Type at least 2 characters.</p>
        ) : isLoading ? (
          <p className="text-sm text-muted-foreground">Searching…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No matching orders.</p>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Buyer</th>
                  <th className="py-2 pr-4 font-medium">Item</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 text-right font-medium">Paid</th>
                  <th className="py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.orderId} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                      {formatDateCompact(r.createdAt)}
                    </td>
                    <td className="py-2 pr-4">
                      <div>{[r.buyerFirstName, r.buyerLastName].filter(Boolean).join(' ') || '—'}</div>
                      <div className="text-xs text-muted-foreground">{r.buyerEmail ?? ''}</div>
                    </td>
                    <td className="py-2 pr-4">{r.classTitle ?? '—'}</td>
                    <td className="py-2 pr-4 capitalize">{r.orderType}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {usd(r.netAmountCents)}
                      {r.refundTotalCents ? (
                        <div className="text-xs text-destructive">−{usd(r.refundTotalCents)} refunded</div>
                      ) : null}
                    </td>
                    <td className="py-2">
                      <Badge variant={r.status === 'paid' ? 'default' : 'outline'} className="capitalize">
                        {r.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
